import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import setExpirationDate from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/SetExpirationDate";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { RfqQuoteDetailsPageContext } from "@insite/content-library/Pages/RfqQuoteDetailsPage";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    expirationDate: state.pages.rfqQuoteDetails.expirationDate,
    expirationDateError: state.pages.rfqQuoteDetails.expirationDateError,
    quoteSettings: getSettingsCollection(state).quoteSettings,
});

const mapDispatchToProps = {
    setExpirationDate,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsInformationStyles {
    wrapper?: InjectableCss;
    expirationDateLabelText?: TypographyProps;
    quoteDetailExpDate?: TypographyProps;
}

export const rfqQuoteDetailsInformationStyles: RfqQuoteDetailsInformationStyles = {
    wrapper: {
        css: css`
            padding-bottom: .625rem;
            font-family: 'Lato',sans-serif;
            font-size: 1rem;
            letter-spacing: 0;
            line-height: 1.5rem;
        `,
    },
    expirationDateLabelText: {
        variant: "h6",
        as: "h2",
        css: css`
            @media print {
                font-size: .75rem;
            }
            margin-bottom: .3125rem;
            margin-right: .3125rem;
            color: #002554;
            font-size: 1rem;
            line height: 1.5rem;
            font-family: 'Roboto', sans-serif;
            font-weight: bold;
            display: inline-block;
            &:after {
                content: ':';
                display: inline-block;
            }
        `,
    },
    quoteDetailExpDate: {
        css: css`
            color: #333F48;
            font-size: 1rem;
            line height: 1.5rem;
            font-family: 'Lato',sans-serif;
    `,
    },
    
};

const styles = rfqQuoteDetailsInformationStyles;

const RfqQuoteDetailsInformation = ({
    quoteState  
}: Props) => {
    const quote = quoteState.value;

    if (!quote) {
        return null;
    }

    const expirationDateLabel = translate(`${quote.isJobQuote ? "Job" : ""} Expiration Date`);

    return (
        <StyledWrapper data-test-selector="rfqQuoteDetailsExpiration_Date_info">
            {!quote.isEditable && quote.expirationDate && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.expirationDateLabelText} id="quoteExpDate">
                        {expirationDateLabel}
                    </Typography>

                    <Typography {...styles.quoteDetailExpDate} aria-labelledby="quoteExpDate">
                        <LocalizedDateTime
                            dateTime={quote.expirationDate}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
                </StyledWrapper>                        
            )} 
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsInformation),
    definition: {
        displayName: "Quote Detail Expiration Date",
        fieldDefinitions: [],
        group: "RFQ Quote Details",
    },
};

export default widgetModule;
